export type Role = 'user' | 'admin' | 'executive' | 'space_manager';

export const DIMENSION_KINDS = [
  'espacio',
  'cliente_interno',
  'direccion',
  'proyecto',
  'categoria',
  'pieza',
  'sede',
] as const;
export type DimensionKind = (typeof DIMENSION_KINDS)[number];

export const DIMENSION_LABELS: Record<DimensionKind, string> = {
  espacio: 'Espacio',
  cliente_interno: 'Cliente interno',
  direccion: 'Dirección',
  proyecto: 'Proyecto',
  categoria: 'Categoría',
  pieza: 'Pieza',
  sede: 'Sede',
};

export const STATUSES = [
  'pending_approval',
  'pending',
  'in_progress',
  'paused',
  'done',
  'cancelled',
  'rejected',
] as const;
export type Status = (typeof STATUSES)[number];

/** Estados del flujo de aprobación (latente; no se ofrecen en la UI). */
export const HIDDEN_STATUSES: string[] = ['pending_approval', 'rejected'];
/** Estados visibles/seleccionables en la UI (chips de filtro, selector del modal). */
export const VISIBLE_STATUSES = STATUSES.filter((s) => !HIDDEN_STATUSES.includes(s));

export const STATUS_LABELS: Record<string, string> = {
  pending_approval: 'Pend. aprobación',
  pending: 'Pendiente',
  in_progress: 'En progreso',
  paused: 'Pausada',
  done: 'Finalizada',
  cancelled: 'Cancelada',
  rejected: 'Rechazada',
};

export const STATUS_COLORS: Record<string, string> = {
  pending_approval: '#a855f7',
  pending: '#64748b',
  in_progress: '#2563eb',
  paused: '#f59e0b',
  done: '#16a34a',
  cancelled: '#94a3b8',
  rejected: '#dc2626',
};

export const PROYECTO_STATUSES = ['planning', 'in_progress', 'paused', 'done', 'cancelled'] as const;
export type ProyectoStatus = (typeof PROYECTO_STATUSES)[number];

export const PROYECTO_STATUS_LABELS: Record<string, string> = {
  planning: 'En planificación',
  in_progress: 'En proceso',
  paused: 'Pausado',
  done: 'Finalizado',
  cancelled: 'Cancelado',
};

export const PROYECTO_STATUS_COLORS: Record<string, string> = {
  planning: '#a855f7',
  in_progress: '#2563eb',
  paused: '#f59e0b',
  done: '#16a34a',
  cancelled: '#94a3b8',
};

/**
 * Largo máximo del título. Lo refuerza el check `backlog_titulo_largo`; acá
 * es el `maxLength` de los inputs, para que el corte pase en el navegador y
 * nadie llegue al error de la base.
 *
 * 81 y no 80 porque es lo que produce el backfill de 20260901120000: 80
 * caracteres de la descripción más la elipsis. Con este tope, las tareas
 * rellenadas entran sin recortarlas de nuevo.
 */
export const TITULO_MAX = 81;

export const TIPOS = ['Ordinaria', 'Extraordinaria'] as const;
export const URGENCIAS = ['baja', 'media', 'alta'] as const;
export const URGENCIA_COLORS: Record<string, string> = {
  baja: '#16a34a',
  media: '#f59e0b',
  alta: '#dc2626',
};
export const DIFICULTADES = ['baja', 'media', 'alta'] as const;

export interface Dimension {
  id: string;
  name: string;
  code: string;
  active: boolean;
  categoria?: string;
  direccion_asociada?: string;
  description?: string;
  urgencia?: string;
  status?: string;
  start_date?: string;
  deadline?: string;
  finished_date?: string;
}

export interface UserDoc {
  email: string;
  first_name: string;
  last_name: string;
  role: Role;
}

export interface BacklogTask {
  id: string;
  espacio: string;
  cliente_interno: string;
  direccion: string;
  proyecto: string;
  categoria: string;
  pieza: string;
  sede: string;
  tipo: string;
  urgencia: string;
  dificultad: string;
  horas_estimadas: number | '';
  horas_reales: number | '';
  ajuste: string;
  ajustes_count: number;
  description: string;
  titulo: string; // título corto obligatorio, máx. TITULO_MAX caracteres
  enlace_documentos: string; // URL opcional a los documentos de la tarea
  status: Status;
  rejected_reason: string;
  fecha_solicitud: string; // fecha del pedido formal (puede diferir del timestamp de carga)
  created_at: string;
  created_by: string;
  assigned_to: string;
  approved_by: string;
  approved_at: string;
  start_date: string;
  deadline: string;
  finished_at: string;
  updated_at: string;
  updated_by: string;
}

export interface TaskComment {
  id: string;
  task_id: string;
  author_email: string;
  body: string;
  created_at: string;
}

export const REVIEW_STATUSES = ['pending', 'disabled', 'reviewed'] as const;
export type ReviewStatus = (typeof REVIEW_STATUSES)[number];

export const REVIEW_VALORACIONES = ['aprobado', 'rechazado'] as const;
export type ReviewValoracion = (typeof REVIEW_VALORACIONES)[number];

export const VALORACION_LABELS: Record<string, string> = {
  aprobado: 'Aprobado',
  rechazado: 'Rechazado',
};

export const VALORACION_COLORS: Record<string, string> = {
  aprobado: '#16a34a',
  rechazado: '#dc2626',
};

export interface Review {
  id: string;
  task_id: string;
  status: ReviewStatus;
  valoracion: string; // '' | 'aprobado' | 'rechazado'
  descripcion: string;
  reviewed_by: string;
  reviewed_at: string;
  created_at: string;
  updated_at: string;
}

export function displayUser(u: UserDoc): string {
  const full = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  return full || u.email;
}
